import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { IConnectionMapping } from '../../models/integration/connection-mapping.model';
import { IIntegrationConnection } from '../../models/integration/integration-connection.model';
import { VENDOR_DEFINITIONS } from '../../models/integration/vendor-definitions.constant';
import { VendorBadgeState } from '../../models/integration/vendor-badge-state.model';
import { VendorDefinition } from '../../models/integration/vendor-definition.model';
import { IntegrationProvider } from '../../provider/integration.provider';

/** Connection state for a per-company vendor (e.g. Bexio), supplied by the page. */
export type PerCompanyVendorState = VendorBadgeState;

/**
 * Company-page view of ALL integrations in one unified Verbunden/Verfügbar split.
 *
 * Two-track by design:
 *   - per-org vendors (D365 BC): state comes from `connectionMappings`, and the
 *     Info button opens the redirect modal owned by THIS lib (managed in Org Settings).
 *   - per-company vendors (Bexio): state lives in aumico-lib-companies-fe, which this
 *     lib does not depend on. The page supplies their state via `perCompanyState` and
 *     handles their Info click via the `perCompanyInfoClick` output (opens its own modal).
 */
@Component({
    selector: 'aumico-company-integrations-overview',
    templateUrl: './company-integrations-overview.component.html',
    styleUrls: ['./company-integrations-overview.component.scss'],
})
export class CompanyIntegrationsOverviewComponent implements OnInit, OnChanges {
    @Input() public companyId!: string;

    /** Per-org mappings from `Company.integrations.connectionMappings`. */
    @Input() public connectionMappings: IConnectionMapping[] = [];

    /**
     * Connection state for per-company vendors, keyed by IntegrationVendor value.
     * The page computes this (e.g. Bexio from BexioProvider) and passes it in.
     */
    @Input() public perCompanyState: Record<string, PerCompanyVendorState> = {};

    /**
     * Whether the current user is an org/app admin. Only admins can reach the Org
     * Settings, so the per-org redirect modal offers them a "go to Org Settings" CTA;
     * non-admins are asked to contact an admin. Resolved by the page.
     */
    @Input() public isAdmin = false;

    /** Fired when a per-company vendor's Info button is clicked. The page opens the modal. */
    @Output() public perCompanyInfoClick = new EventEmitter<VendorDefinition>();

    /** Fired when an admin chose to open the Org Settings for a per-org vendor. */
    @Output() public goToAdmin = new EventEmitter<VendorDefinition>();

    /** All catalog vendors: per-org + per-company, rendered in one unified split. */
    public readonly vendors: VendorDefinition[] = VENDOR_DEFINITIONS;

    /** Per-vendor badge state, rebuilt whenever inputs change. */
    public vendorStates: Record<string, Observable<VendorBadgeState>> = {};

    /** Vendors split into the Verbunden / Verfügbar sections, recomputed on input changes. */
    public connectedVendors: VendorDefinition[] = [];
    public availableVendors: VendorDefinition[] = [];

    // ── Redirect-modal local state (per-org vendors only) ──
    public activeInfoVendor: VendorDefinition | null = null;
    public activeMapping: IConnectionMapping | null = null;
    public activeConnection$: Observable<IIntegrationConnection | null> = of(null);

    public constructor(private readonly integrationProvider: IntegrationProvider) {}

    public ngOnInit(): void {
        // Needed so getConnectionById$ can resolve the tenant behind a per-org mapping.
        this.integrationProvider.loadConnections();
    }

    public ngOnChanges(): void {
        this.rebuildVendorStates();
        this.connectedVendors = this.vendors.filter((v) => this.isConnected(v));
        this.availableVendors = this.vendors.filter((v) => !this.isConnected(v));
    }

    public onInfo(vendor: VendorDefinition): void {
        if (vendor.scope === 'per-company') {
            // Owned by the page (different lib): delegate.
            this.perCompanyInfoClick.emit(vendor);
            return;
        }
        // Per-org: open the redirect modal internally.
        this.activeInfoVendor = vendor;
        const mapping = this.getMapping(vendor) ?? null;
        this.activeMapping = mapping;
        this.activeConnection$ =
            mapping != null
                ? this.integrationProvider.getConnectionById$(mapping.connectionId)
                : of(null);
    }

    public onModalClosed(): void {
        this.activeInfoVendor = null;
        this.activeMapping = null;
        this.activeConnection$ = of(null);
    }

    /** Admin clicked the Org-Settings CTA in the per-org redirect modal. */
    public onGoToAdmin(): void {
        if (this.activeInfoVendor != null) {
            this.goToAdmin.emit(this.activeInfoVendor);
        }
        this.onModalClosed();
    }

    public trackByVendor(_: number, vendor: VendorDefinition): string {
        return vendor.vendor;
    }

    private isConnected(vendor: VendorDefinition): boolean {
        if (vendor.scope === 'per-company') {
            return this.perCompanyState[vendor.vendor]?.connected ?? false;
        }
        return this.getMapping(vendor) != null;
    }

    private getMapping(vendor: VendorDefinition): IConnectionMapping | undefined {
        return this.connectionMappings.find((m) => m.vendor === vendor.vendor);
    }

    private rebuildVendorStates(): void {
        const next: Record<string, Observable<VendorBadgeState>> = {};
        for (const vendor of this.vendors) {
            if (vendor.scope === 'per-company') {
                const state = this.perCompanyState[vendor.vendor];
                next[vendor.vendor] = of({
                    connected: state?.connected ?? false,
                    needsAction: state?.needsAction ?? false,
                });
                continue;
            }
            const mapping = this.getMapping(vendor);
            if (mapping == null) {
                next[vendor.vendor] = of({ connected: false, needsAction: false });
                continue;
            }
            // Connected per-org. Orange "action required" when the mapped connection
            // is missing/unhealthy (validate-on-read).
            next[vendor.vendor] = this.integrationProvider
                .isConnectionHealthy$(mapping.connectionId)
                .pipe(map((healthy) => ({ connected: true, needsAction: !healthy })));
        }
        this.vendorStates = next;
    }
}
